import React, { useState, useRef } from "react";
import Button from "../../../components/common/Button";
import { useTransientMessage } from "hooks/useTransientMessage";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  MdArrowBack,
  MdEdit,
  MdDelete,
  MdCloudUpload,
  MdImage,
  MdEmojiEvents,
} from "react-icons/md";
import { FaCrown } from "react-icons/fa";
import { useProgram, useProgramOperations } from "../../../hooks/usePrograms";
import { ProgramService } from "../../../services/programService";
import {
  TrainingProgramType,
  UpdateProgramInput,
} from "../../../types/program";
import ProgramModal from "../../../components/programs/ProgramModal";
import Card from "components/card";
import DeleteConfirmationModal from "../../../components/modals/DeleteConfirmationModal";
import TrainingWeeks from "../../../components/programs/TrainingWeeks";
import ChallengeAssignmentModal from "../../../components/programs/ChallengeAssignmentModal";
import {
  getDifficultyLevelColor,
  getDifficultyLevelLabel,
  getTrainingProgramTypeColor,
  getTrainingProgramTypeLabel,
  normalizeDifficultyLevels,
} from "../../../utils/programUtils";
import LoadingSpinner from "components/common/LoadingSpinner";

const ProgramDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const { program, loading, error, refresh } = useProgram(
    id ? parseInt(id) : null
  );
  const {
    updateProgram,
    deleteProgram: deleteProgramOperation,
    loading: operationLoading,
    error: operationError,
  } = useProgramOperations();

  // Program states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChallengeModal, setShowChallengeModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [imageError, setImageError] = useTransientMessage(5000);
  const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const difficultyLevels = normalizeDifficultyLevels(
    program?.difficulty_level as any
  );

  const programChallenges = program?.challenges || [];

  const handleBack = () => {
    const returnPath =
      "/admin/programs" + ((location.state as any)?.from || "");
    navigate(returnPath);
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteProgram = async () => {
    if (!program) return;

    const success = await deleteProgramOperation(program.id);
    if (success) {
      const returnPath =
        "/admin/programs" + ((location.state as any)?.from || "");
      navigate(returnPath);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const handleUpdateSubmit = async (programData: any) => {
    if (!program) return;

    const updateData: UpdateProgramInput = {
      name: programData.name,
      program_type: programData.program_type,
      difficulty_level: programData.difficulty_level,
      description: programData.description,
      equipment: programData.equipment,
      is_visible: programData.is_visible,
    };

    const updatedProgram = await updateProgram(program.id, updateData);
    if (updatedProgram) {
      refresh();
      setShowEditModal(false);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const validateImageFile = (file: File | undefined): string | null => {
    if (!file) return "No file selected";

    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      return "Only JPG, JPEG, and PNG files are allowed";
    }

    const maxSize = 20 * 1024 * 1024; // 20MB
    if (file.size > maxSize) {
      return "File size must be less than 20MB";
    }

    return null;
  };

  const handleImageFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file || !program) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setImageError(validationError);
      return;
    }

    setUploadingImage(true);
    setImageError(null);

    try {
      const result = await ProgramService.uploadProgramImage(program.id, file);

      if (result.success) {
        refresh();
        setImageError(null);
      } else {
        setImageError(result.error || "Failed to upload image");
      }
    } catch (error) {
      setImageError("An unexpected error occurred");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteImageClick = () => {
    setShowDeleteImageModal(true);
  };

  const handleConfirmDeleteImage = async () => {
    if (!program?.training_program_image?.id) return;

    setDeletingImage(true);
    setImageError(null);

    try {
      const result = await ProgramService.deleteProgramImage(
        program.id,
        program.training_program_image.id
      );

      if (result.success) {
        refresh();
        setShowDeleteImageModal(false);
        setImageError(null);
      } else {
        setImageError(result.error || "Failed to delete image");
        setShowDeleteImageModal(false);
      }
    } catch (error) {
      setImageError("An unexpected error occurred");
      setShowDeleteImageModal(false);
    } finally {
      setDeletingImage(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error || !program) {
    return (
      <Card extra="w-full p-3 md:p-6">
        <div className="py-12 text-center">
          <div className="mb-4 text-lg text-red-500">
            {error || "Program not found"}
          </div>
          <button
            onClick={handleBack}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
            <span className="hidden md:inline">Back to Programs</span>
          </button>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Actions */}
      <div className="flex items-end justify-between">
        <button
          onClick={handleBack}
          className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
        >
          <MdArrowBack className="h-6 w-6 md:mr-2 md:h-4 md:w-4" />
          <span className="hidden md:inline">Back to Programs</span>
        </button>

        <div className="mt-3 flex flex-wrap justify-end gap-3">
          <button
            onClick={() => setShowChallengeModal(true)}
            className="inline-flex items-center rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700"
          >
            <MdEmojiEvents className="mr-2 h-4 w-4 text-white" />
            Manage Challenges
          </button>
          <Button onClick={handleEdit} disabled={operationLoading}>
            <MdEdit className="mr-2 h-4 w-4" />
            Edit Program
          </Button>
          <button
            onClick={handleDelete}
            disabled={operationLoading}
            className="inline-flex items-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
          >
            <MdDelete className="mr-2 h-4 w-4" />
            Delete Program
          </button>
        </div>
      </div>

      {/* Program Profile Card */}
      <Card extra="w-full p-3 md:p-6">
        <div className="flex flex-col space-y-4 md:flex-row md:items-start md:space-x-6 md:space-y-0">
          {/* Program Image */}
          <div className="flex-shrink-0">
            <div className="group relative">
              {program.training_program_image?.full_path ? (
                <>
                  <img
                    className="h-44 w-72 rounded-lg object-cover"
                    src={program.training_program_image.full_path}
                    alt={program.name}
                  />
                  {/* Hover overlay for delete */}
                  <div className="bg-black absolute inset-0 flex items-center justify-center space-x-2 rounded-lg bg-opacity-50 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={handleImageUploadClick}
                      disabled={uploadingImage || deletingImage}
                      className="rounded-full bg-white p-2 transition-colors hover:bg-gray-100 disabled:opacity-50"
                      title="Change image"
                    >
                      <MdCloudUpload className="h-5 w-5 text-gray-700" />
                    </button>
                    <button
                      onClick={handleDeleteImageClick}
                      disabled={uploadingImage || deletingImage}
                      className="rounded-full bg-red-600 p-2 transition-colors hover:bg-red-700 disabled:opacity-50"
                      title="Delete image"
                    >
                      <MdDelete className="h-5 w-5 text-white" />
                    </button>
                  </div>
                  {uploadingImage && (
                    <div className="bg-black absolute inset-0 flex items-center justify-center rounded-lg bg-opacity-50">
                      <LoadingSpinner bare size="lg" tone="white" />
                    </div>
                  )}
                </>
              ) : (
                <div
                  onClick={handleImageUploadClick}
                  className="flex h-44 w-72 max-w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-100 transition-colors hover:bg-gray-200 dark:border-gray-600 dark:bg-navy-800 dark:hover:bg-navy-700"
                >
                  {uploadingImage ? (
                    <LoadingSpinner bare size="lg" />
                  ) : (
                    <>
                      <MdImage className="mb-2 h-12 w-12 text-gray-400" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Upload Image
                      </span>
                    </>
                  )}
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageFileChange}
                className="hidden"
              />
            </div>

            {/* Image error message */}
            {imageError && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400">
                {imageError}
              </div>
            )}

            {/* Image upload hint */}
            <div className="mt-2 w-full text-left text-xs text-gray-500 dark:text-gray-400 md:text-center">
              Click to upload JPG, JPEG, or PNG (max 20MB)
            </div>
          </div>

          {/* Program Info */}
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex items-center space-x-3">
              <h1 className="flex items-center space-x-2 text-2xl font-bold text-navy-700 dark:text-white">
                {program.is_admin && (
                  <span
                    className="inline-flex h-6 w-6 min-w-6 items-center justify-center rounded-full bg-biafit-primary text-white"
                    aria-label="Admin created program"
                  >
                    <FaCrown className="h-3.5 w-3.5" />
                  </span>
                )}
                <span>{program.name}</span>
              </h1>
            </div>

            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                  Description
                </label>
                <div className="text-navy-700 dark:text-white">
                  {program.description ? (
                    <p className="whitespace-pre-wrap">{program.description}</p>
                  ) : (
                    <p className="italic text-gray-400">
                      No description provided
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Program Information Card */}
      <Card extra="w-full p-3 md:p-6">
        <h2 className="mb-4 text-xl font-bold text-navy-700 dark:text-white">
          Program Information
        </h2>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Program Type
            </label>
            {program.program_type ? (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getTrainingProgramTypeColor(
                  program.program_type as TrainingProgramType
                )}`}
              >
                {getTrainingProgramTypeLabel(
                  program.program_type as TrainingProgramType
                )}
              </span>
            ) : (
              <span className="text-sm italic text-gray-400">
                Not specified
              </span>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Difficulty Level
            </label>
            <div className="flex flex-wrap gap-2">
              {difficultyLevels.length > 0 ? (
                difficultyLevels.map((level) => (
                  <span
                    key={level}
                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getDifficultyLevelColor(
                      level
                    )}`}
                  >
                    {getDifficultyLevelLabel(level)}
                  </span>
                ))
              ) : (
                <span className="text-sm italic text-gray-400">
                  Not specified
                </span>
              )}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Duration
            </label>
            <p className="text-navy-700 dark:text-white">
              {program.duration_weeks} weeks
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Training Days Per Week
            </label>
            <p className="text-navy-700 dark:text-white">
              {program.training_days_per_week || "Not specified"}{" "}
              {program.training_days_per_week &&
                (program.training_days_per_week === 1 ? "day" : "days")}{" "}
              per week
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Workout Duration
            </label>
            <p className="text-navy-700 dark:text-white">
              {program.workout_duration}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Visible to Users
            </label>
            <p className="text-navy-700 dark:text-white">
              {program.is_visible !== undefined
                ? program.is_visible
                  ? "Yes"
                  : "No"
                : "Yes"}
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
              Created Date
            </label>
            <p className="text-navy-700 dark:text-white">
              {new Date(program.created_at).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>

          {program.equipment && program.equipment.length > 0 && (
            <div className="md:col-span-2 lg:col-span-3">
              <label className="mb-2 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Equipment
              </label>
              <div className="flex flex-wrap gap-2">
                {program.equipment.map((item, index) => (
                  <span
                    key={`equipment-${index}`}
                    className="inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}

          {programChallenges.length > 0 && (
            <div className="md:col-span-2 lg:col-span-3">
              <label className="mb-2 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Assigned Challenges
              </label>
              <div className="flex flex-wrap gap-2">
                {programChallenges.map((challenge) => (
                  <span
                    key={`challenge-${challenge.id}`}
                    className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-sm font-medium text-orange-800 dark:bg-orange-900/50 dark:text-orange-200"
                  >
                    {challenge.name} ({challenge.conducted_at_year})
                  </span>
                ))}
              </div>
            </div>
          )}

          {program.image && (
            <div className="md:col-span-2 lg:col-span-3">
              <label className="mb-1 block text-sm font-medium text-gray-500 dark:text-gray-400">
                Image URL
              </label>
              <p className="break-all font-mono text-sm text-navy-700 dark:text-white">
                {program.image}
              </p>
            </div>
          )}
        </div>
      </Card>

      {/* Training Weeks Card */}
      <Card extra="w-full p-3 md:p-6">
        <TrainingWeeks
          programId={program.id}
          trainingDaysPerWeek={program.training_days_per_week}
        />
      </Card>

      {/* Edit Program Modal */}
      <ProgramModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleUpdateSubmit}
        program={program}
        mode="edit"
        loading={operationLoading}
        error={operationError}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDeleteProgram}
        title="Delete Program"
        message="Are you sure you want to delete this training program?"
        itemName={program?.name || "Untitled Program"}
        loading={operationLoading}
        error={operationError}
      />

      {/* Delete Image Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteImageModal}
        onClose={() => setShowDeleteImageModal(false)}
        onConfirm={handleConfirmDeleteImage}
        title="Delete Program Image"
        message="Are you sure you want to delete this program's image?"
        itemName={program?.name || "Program Image"}
        loading={deletingImage}
        error={imageError}
      />

      {/* Challenge Assignment Modal */}
      {program && (
        <ChallengeAssignmentModal
          isOpen={showChallengeModal}
          onClose={() => setShowChallengeModal(false)}
          programId={program.id}
          programName={program.name}
          assignedChallenges={programChallenges}
          onUpdate={refresh}
        />
      )}
    </div>
  );
};

export default ProgramDetail;
